import * as Sentry from "@sentry/react";
import { Component, type ErrorInfo, type ReactNode } from "react";

type Props = {
  /** Drawn in place of the subtree that threw. */
  fallback: ReactNode;
  /** Names the boundary on the Sentry event. */
  scope: string;
  /** When this changes (another thread, another draft) the subtree mounts again. */
  resetKey?: string | number | null;
  children: ReactNode;
};

type State = { failed: boolean; resetKey: Props["resetKey"] };

/**
 * Keeps a render fault inside the part of the page that has it: one bad chat
 * message, a pane, a route. The rest of the page keeps working.
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { failed: false, resetKey: props.resetKey };
  }

  static getDerivedStateFromError(): Partial<State> {
    return { failed: true };
  }

  static getDerivedStateFromProps(
    props: Props,
    state: State
  ): Partial<State> | null {
    return props.resetKey === state.resetKey
      ? null
      : { failed: false, resetKey: props.resetKey };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    const { scope } = this.props;
    Sentry.captureException(error, {
      tags: { boundary: scope },
      extra: { componentStack: info.componentStack }
    });
  }

  render() {
    const { failed } = this.state;
    const { fallback, children } = this.props;
    return failed ? fallback : children;
  }
}

export default ErrorBoundary;
